// ===========================================
// New Students Management Page
// ===========================================
// Displayed when Meet & Greet is DISABLED in onboarding config.
// Manages student onboarding applications: approval, rejection, payment, trials.

import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Grid,
  Skeleton,
  Tooltip,
  Alert,
  TablePagination,
  InputAdornment,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Replay as ReopenIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import {
  useOnboardingList,
  useOnboardingCounts,
  useApproveOnboarding,
  useRejectOnboarding,
  useReopenOnboarding,
} from '../../hooks/useOnboarding';
import type { OnboardingProgress, OnboardingStatus } from '../../api/onboarding.api';
import PageHeader from '../../components/common/PageHeader';

// ===========================================
// STATUS CONFIGURATION
// ===========================================

const statusConfig: Record<
  OnboardingStatus,
  { label: string; bgcolor: string; color: string }
> = {
  IN_PROGRESS: { label: 'In Progress', bgcolor: '#4580E4', color: 'white' },
  AWAITING_APPROVAL: { label: 'Awaiting Approval', bgcolor: '#FFCE00', color: '#080808' },
  AWAITING_PAYMENT: { label: 'Awaiting Payment', bgcolor: '#FFAE9E', color: '#080808' },
  TRIAL_ACTIVE: { label: 'Trial Active', bgcolor: '#96DAC9', color: '#080808' },
  COMPLETED: { label: 'Completed', bgcolor: '#4caf50', color: 'white' },
  REJECTED: { label: 'Rejected', bgcolor: '#ff4040', color: 'white' },
  ABANDONED: { label: 'Abandoned', bgcolor: '#9DA5AF', color: 'white' },
};

type StatusFilterValue = 'ALL' | OnboardingStatus;

const statusTabs: { value: StatusFilterValue; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'AWAITING_APPROVAL', label: 'Awaiting Approval' },
  { value: 'AWAITING_PAYMENT', label: 'Awaiting Payment' },
  { value: 'TRIAL_ACTIVE', label: 'Trial Active' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'REJECTED', label: 'Rejected' },
];

// ===========================================
// STAT CARDS
// ===========================================

function OnboardingStats() {
  const { data: counts, isLoading } = useOnboardingCounts();

  if (isLoading) {
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[...Array(5)].map((_, i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
              <Skeleton variant="text" width="40%" sx={{ mx: 'auto' }} />
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!counts) return null;

  const cards = [
    { label: 'Awaiting Approval', count: counts.awaitingApproval, color: '#FFCE00' },
    { label: 'Awaiting Payment', count: counts.awaitingPayment, color: '#FFAE9E' },
    { label: 'Trial Active', count: counts.trialActive, color: '#96DAC9' },
    { label: 'In Progress', count: counts.inProgress, color: '#4580E4' },
    { label: 'Completed', count: counts.completed, color: '#4caf50' },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card) => (
        <Grid item xs={6} sm={4} md={2.4} key={card.label}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              textAlign: 'center',
              borderTop: `3px solid ${card.color}`,
            }}
          >
            <Typography variant="h4" fontWeight="bold">
              {card.count}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {card.label}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

// ===========================================
// HELPER: Extract student name from registration data
// ===========================================

function getStudentName(progress: OnboardingProgress): string {
  const data = progress.registrationData;
  if (!data) return 'Unknown';
  return `${data.studentFirstName || ''} ${data.studentLastName || ''}`.trim() || 'Unknown';
}

function getParentName(progress: OnboardingProgress): string {
  const data = progress.registrationData;
  if (!data) return '';
  if (progress.isAdultStudent) return '(Adult student)';
  return `${data.parentFirstName || ''} ${data.parentLastName || ''}`.trim();
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function NewStudentsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const filters = {
    status: statusFilter === 'ALL' ? undefined : (statusFilter as OnboardingStatus),
    search: search || undefined,
    page: page + 1,
    limit: rowsPerPage,
  };

  const { data: listResponse, isLoading, refetch } = useOnboardingList(filters);
  const approveMutation = useApproveOnboarding();
  const rejectMutation = useRejectOnboarding();
  const reopenMutation = useReopenOnboarding();

  const records = listResponse?.data ?? [];
  const total = listResponse?.pagination?.total ?? 0;

  const handleTabChange = (_: React.SyntheticEvent, newValue: StatusFilterValue) => {
    setStatusFilter(newValue);
    setPage(0);
  };

  const handleApprove = (id: string) => {
    approveMutation.mutate(id);
  };

  const handleOpenRejectDialog = (id: string) => {
    setRejectTargetId(id);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = () => {
    if (rejectTargetId && rejectReason.trim()) {
      rejectMutation.mutate(
        { id: rejectTargetId, reason: rejectReason },
        { onSuccess: () => setRejectDialogOpen(false) }
      );
    }
  };

  const handleReopen = (id: string) => {
    reopenMutation.mutate(id);
  };

  return (
    <Box>
      <PageHeader
        title="New Students"
        subtitle="Manage student onboarding applications"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'New Students' },
        ]}
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {/* Stats */}
      <OnboardingStats />

      {/* Status Tabs */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={statusFilter}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {statusTabs.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by student name..."
          size="small"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Table */}
      {isLoading ? (
        <Stack spacing={1}>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={50} />
          ))}
        </Stack>
      ) : records.length === 0 ? (
        <Alert severity="info">
          No onboarding records found
          {statusFilter !== 'ALL' ? ` with status "${statusConfig[statusFilter as OnboardingStatus]?.label}"` : ''}.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student Name</TableCell>
                <TableCell>Parent/Contact</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Current Step</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => {
                const sc = statusConfig[record.status];
                return (
                  <TableRow key={record.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {getStudentName(record)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {getParentName(record)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {record.studentAgeAtRegistration ?? '—'}
                        {record.isAdultStudent && (
                          <Chip label="Adult" size="small" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDistanceToNow(new Date(record.startedAt), {
                          addSuffix: true,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {record.currentStep.replace(/([A-Z])/g, ' $1').trim()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={sc.label}
                        size="small"
                        sx={{ bgcolor: sc.bgcolor, color: sc.color, fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {record.status === 'AWAITING_APPROVAL' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                color="success"
                                size="small"
                                onClick={() => handleApprove(record.id)}
                                disabled={approveMutation.isPending}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleOpenRejectDialog(record.id)}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {record.status === 'REJECTED' && (
                          <Tooltip title="Re-open">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleReopen(record.id)}
                              disabled={reopenMutation.isPending}
                            >
                              <ReopenIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </TableContainer>
      )}

      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Student Application</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            placeholder="Please provide a reason for rejecting this application..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || rejectMutation.isPending}
          >
            {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
